import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Currency,
  readCurrencyCookieClient,
  readStoredCurrency,
  writeCurrencyCookie,
  writeStoredCurrency,
} from "@/utils/currency";
import { trackCurrencyChange } from "@/utils/analytics";

// Sewar Gourmet is an Iraqi restaurant — the menu is priced and displayed in Iraqi dinar only.
const IQD: Currency = {
  id: "IQD",
  code: "IQD",
  name_en: "Iraqi Dinar",
  name_ar: "دينار عراقي",
  symbol: "دينار",
  decimal_places: 0,
  is_base: true,
};

const CurrencyContext = createContext<{
  currency: Currency | null;
  setCurrency: (currency: Currency) => void;
} | null>(null);

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [currency, setCurrencyState] = useState<Currency | null>(IQD);

  useEffect(() => {
    // Coerce any stale stored currency (e.g. SYP) back to IQD
    const stored = readStoredCurrency() ?? readCurrencyCookieClient();
    if (stored?.code === "IQD") {
      setCurrencyState(stored);
    } else {
      writeStoredCurrency(IQD);
      writeCurrencyCookie(IQD);
    }
  }, []);

  const setCurrency = (next: Currency) => {
    // Only Iraqi dinar is accepted — ignore foreign currencies
    const fixed = next?.code === "IQD" ? next : IQD;
    trackCurrencyChange(fixed.code || fixed.id || "IQD");
    setCurrencyState(fixed);
    writeStoredCurrency(fixed);
    writeCurrencyCookie(fixed);
    queryClient.invalidateQueries();
  };

  const value = useMemo(() => ({ currency, setCurrency }), [currency]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
};
